import { useEffect } from 'react'
import { Alert, Form, Modal, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format'
import { type Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { updateStockPolicy, type StockPolicy } from './stockApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface Props {
  /**
   * Joriy siyosat; `null` — O'QIB BO'LMADI (`inventory.stock.read` yo'q
   * yoki so'rov yiqildi).
   *
   * ⚠️ `null` ni `0` ga aylantirish TAQIQLANGAN: u "chegara yo'q" degan
   * ISHONCHLI javob bo'lib ko'rinardi, holbuki hech narsa o'qilmagan.
   * Oyna bu holatda maydonni BO'SH ochadi va sababni ochiq yozadi.
   */
  policy: StockPolicy | null
  onClose: () => void
  onDone: (policy: StockPolicy) => void
}

interface FormValues {
  threshold?: Money | null
  internalUseThreshold?: Money | null
}

const requiredMoney = (message: string) => ({
  validator: (_rule: unknown, value: Money | null | undefined) =>
    value === null || value === undefined ? Promise.reject(new Error(message)) : Promise.resolve(),
})

/**
 * Ombor nazorat chegarasi — QIYMAT bo'yicha, miqdor bo'yicha emas.
 *
 * <h2>⚠️ HUQUQ BOSHQA MODULDAN: `settings.update`</h2>
 * Bu jadvaldagi yagona endpoint boshqa modulning huquq kodini ishlatadi
 * va bu ONGLI qaror (`StockResource#updatePolicy`): agar chegarani
 * `inventory.stock.adjust` bilan o'zgartirsa bo'lganda, nazorat MAVJUD
 * BO'LMASDI — nazorat qilinayotgan omborchi chegarani 0 ga tushirib,
 * hisobdan chiqarib, keyin qaytarib qo'ya olardi.
 *
 * ⚠️ Shu sababdan tugmani chizadigan sahifa `settings.update` ni
 * ALOHIDA tekshiradi. `inventory.stock.read` yetarli deb hisoblash
 * `settings.update` siz operatorga tugma berib, uni 403 ga olib
 * borardi — sabab esa ekranda ko'rinmasdi.
 *
 * <h2>⚠️ `0` — TO'LIQ HUQUQIY QIYMAT</h2>
 * U nazoratni O'CHIRADI va bu sukut holat: tashkilot bu mexanizmga
 * BILMASDAN tushmasligi kerak. Shuning uchun forma nolni rad etmaydi,
 * lekin oqibatini OLDINDAN aytadi.
 *
 * <h2>⚠️ CHEGARA KUTAYOTGAN SO'ROVLARGA TEGMAYDI</h2>
 * Har so'rovda o'sha paytdagi chegara SAQLANADI
 * (`stock_approval.threshold_minor`). Chegarani tushirish kutayotgan
 * so'rovni o'z-o'zidan tasdiqlamaydi va ko'tarish uni bekor qilmaydi —
 * ular baribir imzo kutadi. Buni aytmaslik "chegarani nolga tushirsam
 * hammasi o'tib ketadi" degan xato kutilma qoldirardi.
 */
export function StockPolicyModal({ policy, onClose, onDone }: Props) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  useEffect(() => {
    form.setFieldsValue({
      threshold: policy === null ? null : policy.threshold,
      internalUseThreshold: policy === null ? null : policy.internalUseThreshold,
    })
  }, [policy, form])

  const save = useMutation({
    mutationFn: (values: FormValues): Promise<StockPolicy> =>
      // ⚠️ MINOR birlik. `MoneyInput` yagona aylantirish joyi; bu yerda
      // qiymat allaqachon butun son.
      updateStockPolicy(values.threshold?.amount ?? 0, values.internalUseThreshold?.amount ?? 0),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stock.policy })
      onDone(updated)
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open
      title={t('stock.policy.editTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('stock.policy.whyValueNote')}
      />

      {/* Xato OYNANING ichida (`guards.mjs` 1-bo'limi): 403 bu yerda
          «sizda settings.update yo'q» degan aniq matn bilan keladi. */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      {policy === null ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('stock.policy.unknownCurrent')}
          description={t('stock.policy.unknownCurrentHint')}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="threshold"
          label={t('stock.policy.threshold')}
          extra={t('stock.policy.thresholdHint')}
          rules={[requiredMoney(t('stock.policy.validation.thresholdRequired'))]}
        >
          <MoneyInput currency={currency} minMinor={0} />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev: FormValues, next: FormValues) =>
            prev.threshold?.amount !== next.threshold?.amount
          }
        >
          {({ getFieldValue }) => {
            const value = getFieldValue('threshold') as Money | null | undefined
            // ⚠️ `undefined` (hali yozilmagan) va `0` (ataylab
            // o'chirilmoqda) ARALASHTIRILMAYDI: birinchisida hech
            // narsa deyilmaydi, ikkinchisida oqibat aytiladi.
            if (value === null || value === undefined) return null
            return value.amount === 0 ? (
              <Alert type="warning" showIcon message={t('stock.policy.zeroWarning')} />
            ) : (
              <Typography.Text type="secondary">{t('stock.policy.pendingUnaffected')}</Typography.Text>
            )
          }}
        </Form.Item>

        <Form.Item
          name="internalUseThreshold"
          label={t('stock.policy.internalUseThreshold')}
          extra={t('stock.policy.internalUseThresholdHint')}
          style={{ marginTop: 16 }}
          rules={[requiredMoney(t('stock.policy.validation.internalUseThresholdRequired'))]}
        >
          <MoneyInput currency={currency} minMinor={0} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
